import { NoteDetail } from "creso-core/types";
import { PlusIcon, SparklesIcon } from "lucide-react";
import { Button, Checkbox, cn } from "web-design-system";
import { useNote } from "creso-core/hooks";
import { Activity, useMemo, useState } from "react";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { format } from "date-fns";
import { NoteCard } from "./note-card";
import { NoteTaskItem } from "./note-task-item";
import { useNotebookContext } from "./notebook-context";
import { TaskEditor } from "./task-editor";
import { transformNoteListToNoteDetailItem } from "./utils";

export function NoteSection({ showEmpty }: { showEmpty: boolean }) {
  const { selectedNote: note, recordingStatus } = useNotebookContext();

  const { data, isLoading } = useNote(
    note?.id || -1,
    !!note?.id && note.id !== -1
  );

  const noteDetails = useMemo(() => {
    if (note?.id === -1) {
      return transformNoteListToNoteDetailItem(note);
    }
    return data;
  }, [data, note]);

  const [isAddingTask, setIsAddingTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);

  const containerClassName = cn(
    "flex-1 flex flex-col bg-background border border-border p-3 rounded-lg overflow-auto",
    (recordingStatus !== "idle" || note?.id === -1) && "bg-secondary",
    showEmpty && "border-dashed"
  );

  if (showEmpty) {
    return (
      <div className={containerClassName}>
        <NoteSectionEmpty />
      </div>
    );
  }

  if (!note) return null;

  if (isLoading) {
    return (
      <div className={containerClassName}>
        <NoteSectionSkeleton />
      </div>
    );
  }

  if (!noteDetails) return null;

  return (
    <div className={containerClassName}>
      <NoteCard
        note={noteDetails}
        collapseEditing={isAddingTask || editingTaskId !== null}
      />
      <NoteTaskSection
        note={noteDetails}
        isAdding={isAddingTask}
        onAddingChange={(v) => {
          setIsAddingTask(v);
        }}
        editingTaskId={editingTaskId}
        onEditingTaskIdChange={(id) => {
          setIsAddingTask(false);
          setEditingTaskId(id);
        }}
      />
    </div>
  );
}

function NoteSectionEmpty() {
  return (
    <div className="flex flex-col p-3 gap-6">
      <div className="flex flex-col gap-2">
        <span className="text-xs text-muted-foreground">Note from Creso</span>
        <span className="text-sm font-semibold">
          Start dictating quick notes from client interactions
        </span>
      </div>
      <span className="text-sm text-muted-foreground">
        Our in built AI-powered notetaker allows you to jot down your thoughts
        the way you want, and we’ll organize and create tasks to help you keep
        up with client interactions.
      </span>
      <div className="flex flex-col gap-6">
        <div className="flex flex-row gap-2 items-center">
          <SparklesIcon className="size-4 text-muted-foreground" />
          <span className="text-sm font-semibold">1 task added</span>
        </div>
        <div className="flex flex-row gap-4 items-start">
          <Checkbox disabled className="mt-0.5 shrink-0" />
          <div className="flex flex-col gap-2 flex-1">
            <span className="text-sm">Create your first note</span>
            <span className="text-xs font-semibold text-muted-foreground">
              {format(new Date(), "PPP")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function NoteSectionSkeleton() {
  return (
    <div className="flex flex-col p-3 gap-6">
      <div className="flex flex-col gap-2">
        <div className="h-4 w-48 bg-secondary/60 animate-pulse rounded" />
        <div className="h-7 w-2/3 bg-secondary/60 animate-pulse rounded" />
      </div>
      <div className="h-28 w-full bg-secondary/60 animate-pulse rounded-lg" />
      <div className="flex flex-col gap-6">
        <div className="flex flex-row gap-2 items-center">
          <SparklesIcon className="size-4 text-secondary/60" />
          <div className="h-4 w-40 bg-secondary/60 animate-pulse rounded" />
        </div>
        <div className="flex flex-row gap-2 items-start">
          <Checkbox disabled className="shrink-0 border-border opacity-10" />
          <div className="flex flex-col gap-2 flex-1">
            <div className="h-4 bg-secondary/60 animate-pulse rounded w-80" />
            <div className="h-4 w-32 bg-secondary/60 animate-pulse rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}

function NoteTaskSection(props: {
  note: NoteDetail;
  isAdding: boolean;
  onAddingChange: (value: boolean) => void;
  editingTaskId: number | null;
  onEditingTaskIdChange: (id: number | null) => void;
}) {
  const {
    note,
    isAdding,
    onAddingChange,
    editingTaskId,
    onEditingTaskIdChange,
  } = props;

  if (note.id === -1) return null;

  if (!note.tasks) return null;

  return (
    <div className="flex flex-col gap-6 px-3">
      <div className="flex flex-row gap-2 h-9 sticky top-0 items-center">
        <SparklesIcon className="size-4" />
        <span className="font-semibold text-sm">
          {note.tasks.length > 0
            ? `${note.tasks.length} added to your to-dos`
            : "No tasks found"}
        </span>
        <Activity mode={isAdding ? "hidden" : "visible"}>
          <Button
            onClick={() => onAddingChange(true)}
            className="ml-auto -mr-2"
            variant="ghost"
          >
            <PlusIcon /> Add task
          </Button>
        </Activity>
      </div>
      <ConditionalRenderer condition={isAdding}>
        <TaskEditor onFinish={() => onAddingChange(false)} />
      </ConditionalRenderer>
      {note.tasks.map((t) => (
        <NoteTaskItem
          key={t.id}
          task={t}
          isEditing={editingTaskId === t.id}
          onEditStart={() => onEditingTaskIdChange(t.id)}
          onEditEnd={() => onEditingTaskIdChange(null)}
        />
      ))}
    </div>
  );
}
